import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { normalizeUrl } from '@/utils/urlUtils';

export interface CurrentPageState {
  value: string;
  step: number;
  isGoingBack: boolean;
  isLoading: boolean;
  completedSteps: number[];
  isResume: boolean;
  openSoftPull: boolean;
  currentPage: number;
  backPage: number;
  isRedirectImmediately: boolean;
  errorResponsePersisted: string | null;
  isNewApplication: boolean;
  openBackButtonErrorModal: boolean;
  isFirstTimeGetStarted: boolean;
  isFirstTimeGetStartedAddress: boolean;
  isFirstTimeVMO: boolean;
  isFirstTimeEmploymentInformation: boolean;
  isFirstTimePersonalInformation: boolean;
  isFirstTimeGovernmentMonitoringInformation: boolean;
  openWarningChangesModal: boolean;
  openWarningChangesEmploymentModal: boolean;
  isOpenResume: boolean;
  skippedVMO: boolean;
  hasRepriced: boolean;
  shouldForceEmploymentUpdate: boolean;
  originalSensitiveFields: {
    firstName: string;
    lastName: string;
    dob: string;
  };
  originalStatedCreditScore: number | null;
  captchaToken: string | null;
  captchaValidated: boolean;
  captchaTimestamp: number | null;
  hasCalledRatesForVmoSinceResume: boolean;
  boappLastStep: string;
}

const initialState: CurrentPageState = {
  value: '',
  step: 0,
  isGoingBack: false,
  isLoading: false,
  completedSteps: [],
  isResume: false,
  openSoftPull: false,
  currentPage: 0,
  backPage: 0,
  isRedirectImmediately: false,
  errorResponsePersisted: null,
  isNewApplication: false,
  openBackButtonErrorModal: false,
  isFirstTimeGetStarted: true,
  isFirstTimeGetStartedAddress: true,
  isFirstTimeVMO: true,
  isFirstTimeEmploymentInformation: true,
  isFirstTimePersonalInformation: true,
  isFirstTimeGovernmentMonitoringInformation: true,
  openWarningChangesModal: false,
  openWarningChangesEmploymentModal: false,
  isOpenResume: false,
  skippedVMO: false,
  hasRepriced: false,
  shouldForceEmploymentUpdate: false,
  originalSensitiveFields: {
    firstName: '',
    lastName: '',
    dob: '',
  },
  originalStatedCreditScore: null,
  captchaToken: null,
  captchaValidated: false,
  captchaTimestamp: null,
  hasCalledRatesForVmoSinceResume: false,
  boappLastStep: '',
};

const CurrentPageSlice = createSlice({
  name: 'CurrentPage',
  initialState,
  reducers: {
    addCurrentPage: (state, action: PayloadAction<string>) => {
      const currentUrl = sessionStorage.getItem('lastVisitedUrl');
      if (currentUrl) {
        sessionStorage.setItem('previousVisitedUrl', normalizeUrl(currentUrl));
      }
      state.value = action.payload;
      const normalizedCurrentUrl = normalizeUrl(window.location.href);
      sessionStorage.setItem('lastVisitedUrl', normalizedCurrentUrl);

      //storing in history state for access in 'popstate' event handler (protectedRoute, Back button)
      window.history.replaceState(
        {
          ...window.history.state,
          lastVisitedUrl: normalizedCurrentUrl,
        },
        '',
        window.location.href,
      );
    },
    incrementStep: (state) => {
      if (!state.isGoingBack) {
        state.step += 1;
      }
      state.isGoingBack = false;
    },
    decrementStep: (state) => {
      state.step -= 1;
      state.isGoingBack = true;
    },
    setIsGoingBack: (state, action: PayloadAction<boolean>) => {
      state.isGoingBack = action.payload;
    },
    resetGoingBack: (state) => {
      state.isGoingBack = false;
    },
    startLoading: (state) => {
      state.isLoading = true;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    addCompletedStep: (state, action: PayloadAction<number>) => {
      state.completedSteps.push(action.payload);
    },
    setIsResume: (state, action: PayloadAction<boolean>) => {
      state.isResume = action.payload;
      if (action.payload === true) {
        state.hasCalledRatesForVmoSinceResume = false;
      }
    },
    setHasCalledRatesForVmoSinceResume: (state, action: PayloadAction<boolean>) => {
      state.hasCalledRatesForVmoSinceResume = action.payload;
    },
    setOpenSoftPull: (state, action: PayloadAction<boolean>) => {
      state.openSoftPull = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setBackPage: (state, action: PayloadAction<number>) => {
      state.backPage = action.payload;
    },
    setRedirectImmediately: (state, action: PayloadAction<boolean>) => {
      state.isRedirectImmediately = action.payload;
    },
    setErrorResponsePersisted: (state, action: PayloadAction<string | null>) => {
      state.errorResponsePersisted = action.payload;
    },
    setIsNewApplication: (state, action) => {
      state.isNewApplication = action.payload;
    },
    setOpenBackButtonErrorModal: (state, action: PayloadAction<boolean>) => {
      state.openBackButtonErrorModal = action.payload;
    },
    setIsFirstTimeGetStarted: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeGetStarted = action.payload;
    },
    setIsFirstTimeGetStartedAddress: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeGetStartedAddress = action.payload;
    },
    setIsFirstTimeVMO: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeVMO = action.payload;
    },
    setOpenWarningChangesModal: (state, action: PayloadAction<boolean>) => {
      state.openWarningChangesModal = action.payload;
    },
    setIsFirstTimeEmploymentInformation: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeEmploymentInformation = action.payload;
    },
    setIsFirstTimePersonalInformation: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimePersonalInformation = action.payload;
    },
    setIsFirstTimeGovernmentMonitoringInformation: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeGovernmentMonitoringInformation = action.payload;
    },
    setOpenWarningChangesEmploymentModal: (state, action: PayloadAction<boolean>) => {
      state.openWarningChangesEmploymentModal = action.payload;
    },
    setSkippedVMO: (state, action: PayloadAction<boolean>) => {
      state.skippedVMO = action.payload;
    },
    setIsOpenResume: (state, action: PayloadAction<boolean>) => {
      state.isOpenResume = action.payload;
    },
    setHasRepriced: (state, action: PayloadAction<boolean>) => {
      state.hasRepriced = action.payload;
    },
    setShouldForceEmploymentUpdate: (state, action: PayloadAction<boolean>) => {
      state.shouldForceEmploymentUpdate = action.payload;
    },
    setOriginalSensitiveFields: (state, action: PayloadAction<{ firstName: string; lastName: string; dob: string }>) => {
      state.originalSensitiveFields = action.payload;
    },
    setOriginalStatedCreditScore: (state, action: PayloadAction<number | null>) => {
      state.originalStatedCreditScore = action.payload;
    },
    setCaptchaToken: (state, action: PayloadAction<{ token: string; validated: boolean; timestamp: number }>) => {
      state.captchaToken = action.payload.token;
      state.captchaValidated = action.payload.validated;
      state.captchaTimestamp = action.payload.timestamp;
    },
    resetCaptchaToken: (state) => {
      state.captchaToken = null;
      state.captchaValidated = false;
      state.captchaTimestamp = null;
    },
    setBoappLastStep: (state, action: PayloadAction<string>) => {
      state.boappLastStep = action.payload;
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const {
  addCurrentPage,
  incrementStep,
  decrementStep,

  resetGoingBack,
  startLoading,
  stopLoading,

  setIsResume,
  setHasCalledRatesForVmoSinceResume,
  setOpenSoftPull,
  setCurrentPage,
  setBackPage,
  setRedirectImmediately,

  setOpenBackButtonErrorModal,
  setOpenWarningChangesModal,
  setIsFirstTimeGetStarted,
  setIsFirstTimeGetStartedAddress,
  setIsFirstTimeVMO,
  setIsFirstTimeEmploymentInformation,
  setIsFirstTimePersonalInformation,
  setIsFirstTimeGovernmentMonitoringInformation,
  setOpenWarningChangesEmploymentModal,
  setSkippedVMO,
  setIsOpenResume,
  setHasRepriced,
  setShouldForceEmploymentUpdate,
  setOriginalSensitiveFields,
  setOriginalStatedCreditScore,
  setCaptchaToken,
  resetCaptchaToken,
  setBoappLastStep,
} = CurrentPageSlice.actions;

export const selectStep = (state: RootState) => state.currentPage.step;
export const selectIsGoingBack = (state: RootState) => state.currentPage.isGoingBack;
export const selectIsLoading = (state: RootState) => state.currentPage.isLoading;
export const selectCurrentPageNumber = (state: RootState) => state.currentPage.currentPage;
export const selectBackPage = (state: RootState) => state.currentPage.backPage;
export const selectIsRedirectImmediately = (state: RootState) => state.currentPage.isRedirectImmediately;
export const selectErrorResponsePersisted = (state: RootState) => state.currentPage.errorResponsePersisted;
export const selectOpenBackButtonErrorModal = (state: RootState) => state.currentPage.openBackButtonErrorModal;
export const selectOpenWarningChangesModal = (state: RootState) => state.currentPage.openWarningChangesModal;
export const selectIsFirstTimeGetStarted = (state: RootState) => state.currentPage.isFirstTimeGetStarted;
export const selectIsFirstTimeGetStartedAddress = (state: RootState) => state.currentPage.isFirstTimeGetStartedAddress;
export const selectIsFirstTimeVMO = (state: RootState) => state.currentPage.isFirstTimeVMO;
export const selectIsFirstTimeEmploymentInformation = (state: RootState) => state.currentPage.isFirstTimeEmploymentInformation;
export const selectIsFirstTimePersonalInformation = (state: RootState) => state.currentPage.isFirstTimePersonalInformation;
export const selectIsFirstTimeGovernmentMonitoringInformation = (state: RootState) => state.currentPage.isFirstTimeGovernmentMonitoringInformation;
export const selectOpenWarningChangesEmploymentModal = (state: RootState) => state.currentPage.openWarningChangesEmploymentModal;
export const selectIsOpenResume = (state: RootState) => state.currentPage.isOpenResume;
export const selectSkippedVMO = (state: RootState) => state.currentPage.skippedVMO ?? false;
export const selectHasRepriced = (state: RootState) => state.currentPage.hasRepriced;
export const selectShouldForceEmploymentUpdate = (state: RootState) => state.currentPage.shouldForceEmploymentUpdate;
export const selectOriginalSensitiveFields = (state: RootState) => state.currentPage.originalSensitiveFields;
export const selectOriginalStatedCreditScore = (state: RootState) => state.currentPage.originalStatedCreditScore;
export const selectCaptchaToken = (state: RootState) => state.currentPage.captchaToken;
export const selectCaptchaValidated = (state: RootState) => state.currentPage.captchaValidated;
export default CurrentPageSlice.reducer;
